/**
 * Shared assembly for families 2–4 (plan §4.2): boards laid out by a family generator (in
 * ticks, like the case generator's `CaseBoard`) become a `BuildModel` — the substrate plus the
 * first slice's fields every downstream reader expects — with stock fit, glue-ups, part text,
 * substrate validation and the manufacturing hash done once.
 *
 * The grid family keeps its own path (src/families/grid-bookcase/generate.ts) so its pinned
 * goldens stay byte-for-byte; this module copies its rules for stock fit and part text.
 */
import type { BuildModel, Design, Issue, OpeningRow, Part, SheetFit } from '../domain/schemas';
import { SCHEMA_VERSION } from '../domain/schemas';
import { limitsOf, type ConstructionProfile } from '../domain/profiles';
import { hashOf } from '../domain/hash';
import { fmtMm, fmtNum, toMm } from '../domain/units';
import type { Datum, Handle, Hardware, IssueFix, Joint, JointKind, StockUse } from './assembly';
import { manufacturingHash } from './hash';
import { faceText, resolveStock, type AnyStock, type ResolvedStock } from './stock';
import { validateSubstrate } from './validate';
import { buildFronts, frontsOf, FRONTS_PROFILE, type FrontKind } from './fronts';

/** Joints the maker sees on the finished piece (drawn by the kit). */
const VISIBLE_JOINTS = new Set<JointKind>(['housed-dado', 'rebate', 'egg-crate', 'mortise-tenon', 'through-tenon', 'mitre']);

export const FAMILIES_GENERATOR_VERSION = 'cutlist-maker 0.3.0 · families 1.1';

/** A board as a family generator lays it out (ticks; origin floor / left / back). */
export interface FamilyBoard {
  id: string;
  role: string;
  label: string;
  /** Along the grain, across it, thickness (ticks). */
  l: number;
  w: number;
  t: number;
  grainWorld: 'x' | 'y' | 'z';
  box: { x: number; y: number; z: number; sx: number; sy: number; sz: number };
  /** 'carcass' boards come from the carcass stock, 'back' from the back sheet. */
  stock: 'carcass' | 'back';
  datum?: number;
  shelfNo?: number;
  machining?: string;
  /** Face text side: 'out' for verticals, 'up' for horizontals (default from grain). */
  faceSide?: 'out' | 'up';
}

export interface FamilyJoint { a: string; b: string; kind: JointKind; face: string; datum: number }

export interface FamilyOpening { id: string; label: string; x0: number; x1: number; y0: number; y1: number }

export interface AssembleInput {
  familyId: string;
  familyVersion: string;
  familyLabel: string;
  design: Design;
  stock: AnyStock;
  construction: ConstructionProfile;
  boards: FamilyBoard[];
  joints: FamilyJoint[];
  openings: FamilyOpening[];
  /** Horizontal datums (undersides from the floor, ticks), bottom-up. */
  datums: { partId: string; label: string; ticks: number }[];
  errors: Issue[];
  warnings: Issue[];
  hardware?: Hardware[];
  handles?: Handle[];
  /** Clear width shown in the bay summary (ticks). */
  clearWidth: number;
  /** Count shown as "shelves" in the summary. */
  shelves: number;
}

interface FitCheck { fits: boolean; rotated: boolean; note: string; glueUp?: { strips: number; stripWidthTicks: number } }

function sheetFit(l: number, w: number, sheet: { lengthTicks: number; widthTicks: number }, grainAxis: 'length' | 'width', allowRotation: boolean, a: number): FitCheck {
  const sheetL = sheet.lengthTicks - 2 * a;
  const sheetW = sheet.widthTicks - 2 * a;
  const alongL = grainAxis === 'length' ? sheetL : sheetW;
  const acrossL = grainAxis === 'length' ? sheetW : sheetL;
  const desc = `${fmtMm(sheet.lengthTicks)} × ${fmtMm(sheet.widthTicks)}`;
  const straight = l <= alongL && w <= acrossL;
  const rotated = !straight && allowRotation && l <= acrossL && w <= alongL;
  return {
    fits: straight || rotated, rotated,
    note: straight
      ? `Fits one ${desc} sheet with the grain along its length.`
      : rotated
        ? `Fits one ${desc} sheet only with the grain across it.`
        : `Longer than one sheet allows with the grain along its length (max ${fmtMm(sheetL)} × ${fmtMm(sheetW)} mm).`,
  };
}

/** Board stock: one board, or a glue-up of `ceil(w / maxWidth)` strips (plan §5). */
export function boardFit(l: number, w: number, R: ResolvedStock): FitCheck {
  const maxL = R.maxLengthTicks ?? Infinity;
  const maxW = R.maxWidthTicks ?? Infinity;
  if (l > maxL) return { fits: false, rotated: false, note: `Longer than the longest ${R.species.toLowerCase()} board (${fmtMm(maxL)} mm).` };
  if (w > maxW) {
    const strips = Math.ceil(w / maxW);
    const stripWidthTicks = Math.ceil(w / strips);
    return { fits: true, rotated: false, glueUp: { strips, stripWidthTicks }, note: `Glued up from ${strips} strips of ${fmtMm(stripWidthTicks)} mm (boards are sold up to ${fmtMm(maxW)} mm wide).` };
  }
  return { fits: true, rotated: false, note: `From one ${R.species.toLowerCase()} board, ${fmtMm(R.thicknessTicks)} mm thick.` };
}

/** Equal split of `total` ticks into `count` parts, residual ticks on the lowest first (the grid's rule). */
export function evenSplit(total: number, count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(total / count);
  const rem = total - base * count;
  return Array.from({ length: count }, (_x, i) => base + (i < rem ? 1 : 0));
}

/** Split `total` ticks by weights: each rounded to whole millimetres, the residual on the first. */
export function splitByWeights(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (!weights.length || !(sum > 0)) return evenSplit(total, weights.length);
  const g = weights.map((w) => Math.round((total / 10) * (w / sum)) * 10);
  g[0]! += total - g.reduce((a, b) => a + b, 0);
  return g;
}

/**
 * A fraction of W (or H) in ticks, rounded to whole millimetres and kept inside 0…total.
 *
 * FND-03: a width that is not a whole number of millimetres (35.5 in = 901.7 mm) used to round
 * `xEnd = 1` up to 902 mm, 0.3 mm past the overall size, so every full-width board "lay outside
 * the overall size". The ends of the range are exact and nothing rounds past them; for whole-mm
 * totals the result is unchanged (goldens stay byte-for-byte).
 */
export const fracTicks = (total: number, f: number): number => {
  if (!(f > 0)) return 0;
  if (f >= 1) return total;
  return Math.min(total, Math.max(0, Math.round((total / 10) * f) * 10));
};

export function assembleBuild(x: AssembleInput): BuildModel {
  const R = resolveStock(x.stock);
  const c = x.construction;
  const { W, H, D } = x.design;
  const errors = x.errors.slice();
  const a = c.edgeAllowanceTicks;
  const cutL = c.cutAllowanceTicks.l;
  const cutW = c.cutAllowanceTicks.w;
  const arris = fmtNum(c.arrisMm);
  const hasBack = x.boards.some((q) => q.stock === 'back');
  const fitByPart = new Map<string, FitCheck>();
  const parts: Part[] = x.boards.map((q) => {
    const isBack = q.stock === 'back';
    const fit = isBack
      ? sheetFit(q.l + cutL, q.w + cutW, R.back.sheet, R.back.grainAxis, R.back.allowRotation, a)
      : R.kind === 'sheet'
        ? sheetFit(q.l + cutL, q.w + cutW, R.sheet!, R.back.grainAxis, R.allowRotation, a)
        : boardFit(q.l + cutL, q.w + cutW, R);
    fitByPart.set(q.id, fit);
    const side = q.faceSide ?? (q.grainWorld === 'y' ? 'out' : 'up');
    const face = isBack ? R.back.faceText : faceText(R, side);
    const edge = (isBack ? `Edges hidden behind the carcass; sanded arris ${arris} mm` : R.edgeText(arris)) + (q.machining ? `; ${q.machining}` : '');
    const part: Part = {
      id: q.id, role: q.role, label: q.label, qty: 1,
      material: isBack ? R.back.label : R.label,
      stockId: isBack ? `${R.id}:back` : R.id,
      thicknessTicks: q.t,
      finished: { l: q.l, w: q.w, t: q.t },
      cut: { l: q.l + cutL, w: q.w + cutW, t: q.t },
      grainAxis: 'l',
      grainWorld: q.grainWorld,
      face, edge,
      placement: { x: toMm(q.box.x), y: toMm(q.box.y), z: toMm(q.box.z), sx: toMm(q.box.sx), sy: toMm(q.box.sy), sz: toMm(q.box.sz) },
    };
    if (fit.glueUp) part.glueUp = fit.glueUp;
    if (q.datum !== undefined) part.datum = toMm(q.datum);
    if (q.shelfNo !== undefined) part.shelfNo = q.shelfNo;
    return part;
  });

  const stockFit: SheetFit[] = parts.map((p) => {
    const f = fitByPart.get(p.id)!;
    return { partId: p.id, fits: f.fits, rotationUsed: f.rotated, note: f.note };
  });
  for (const f of stockFit) {
    if (f.fits) continue;
    const p = parts.find((q) => q.id === f.partId)!;
    errors.push({ field: p.id, message: `${p.label}: ${f.note.charAt(0).toLowerCase()}${f.note.slice(1)}` });
  }

  const joints: Joint[] = x.joints.map((j) => ({ id: `${j.a}~${j.b}`, a: j.a, b: j.b, kind: j.kind, face: j.face, datumMm: toMm(j.datum), visible: VISIBLE_JOINTS.has(j.kind) }));
  const openings: OpeningRow[] = x.openings.map((o, i) => ({
    id: o.id, label: o.label, bounds: { x0: toMm(o.x0), x1: toMm(o.x1), y0: toMm(o.y0), y1: toMm(o.y1) },
    clearMm: toMm(o.y1 - o.y0), index: i, number: i + 1, clear: o.y1 - o.y0, bottomDatum: toMm(o.y0), topDatum: toMm(o.y1),
  }));
  const datums: Datum[] = x.datums.map((d) => ({ partId: d.partId, label: d.label, mm: toMm(d.ticks) }));

  // doors and drawers (fronts.ts): absent for a design without fronts, which builds exactly as before
  const fr = frontsFor(x.design, parts, openings, R, c);
  const hardware = [...(x.hardware ?? []), ...(fr?.hardware ?? [])];
  if (fr) { parts.push(...fr.parts); stockFit.push(...fr.fits); joints.push(...fr.joints); errors.push(...fr.errors); }
  const Dall = D + (fr?.depthAdd ?? 0);
  const errorFields = new Set(errors.map((e) => e.field));
  const warnings = [...x.warnings, ...(fr?.warnings ?? [])].filter((w) => !errorFields.has(w.field));
  if (!errors.length) {
    const sub = validateSubstrate({ parts, joints, envelope: { W: toMm(W), H: toMm(H), D: toMm(Dall) }, stockFit: [], hardware });
    errors.push(...sub.errors);
  }

  const sheetStock = R.sheet ?? R.back.sheet;
  const stocks: StockUse[] = [
    {
      id: R.id, version: R.version, kind: R.kind, material: R.material, label: R.label, thicknessTicks: R.thicknessTicks,
      ...(R.sheet ? { sheet: R.sheet } : {}), ...(R.maxWidthTicks ? { maxWidthTicks: R.maxWidthTicks } : {}), ...(R.density ? { density: R.density } : {}),
    },
    ...(hasBack || parts.some((q) => q.stockId === `${R.id}:back`) ? [{ id: `${R.id}:back`, version: R.version, kind: 'sheet' as const, material: R.kind === 'sheet' ? R.material : 'Plywood', label: R.back.label, thicknessTicks: R.back.thicknessTicks, sheet: R.back.sheet, density: 680 }] : []),
    ...(fr?.stocks ?? []),
  ];
  const openingsSum = openings.reduce((s, o) => s + o.clear, 0);
  const jointsVersion = `${c.id}@${c.version}`;
  return {
    schemaVersion: SCHEMA_VERSION,
    familyId: x.familyId,
    familyVersion: x.familyVersion,
    familyLabel: x.familyLabel,
    family: x.familyId,
    design: JSON.parse(JSON.stringify(x.design)) as Design,
    stock: x.stock as BuildModel['stock'],
    construction: c,
    thickness: { carcass: toMm(R.thicknessTicks), back: hasBack ? toMm(R.back.thicknessTicks) : 0 },
    parts,
    joints,
    hardware,
    openings,
    handles: x.handles ?? [],
    datums,
    shelfDatums: datums.map((d) => d.mm),
    available: openingsSum, availableMm: toMm(openingsSum), openingsSum, remainder: 0,
    bays: [{ index: 0, clearWidth: x.clearWidth, n: x.shelves, available: openingsSum, openingsSum, remainder: 0 }],
    envelope: { W: toMm(W), H: toMm(H), D: toMm(Dall), limits: limitsOf(c) },
    stocks,
    sheet: { length: toMm(sheetStock.lengthTicks), width: toMm(sheetStock.widthTicks), usableLength: toMm(sheetStock.lengthTicks - 2 * a), usableWidth: toMm(sheetStock.widthTicks - 2 * a) },
    sheetFit: stockFit,
    stockFit,
    panelCount: parts.length,
    validation: { ok: errors.length === 0, errors, warnings },
    versions: { family: `${x.familyId}@${x.familyVersion}`, stock: `${R.id}@${R.version}`, joints: jointsVersion, construction: jointsVersion, generator: FAMILIES_GENERATOR_VERSION },
    manufacturingHash: manufacturingHash({ parts, openings, joints, hardware }),
    appearanceHash: hashOf(x.design.appearance ?? {}),
  };
}

/** The drawer-box ply sheet (fronts-profile.json `drawerBox`): a standard 2440 × 1220 sheet. */
const DRAWER_SHEET = { lengthTicks: 24400, widthTicks: 12200 };

export interface FrontsResult {
  parts: Part[];
  fits: SheetFit[];
  joints: Joint[];
  hardware: Hardware[];
  errors: Issue[];
  warnings: Issue[];
  /** How far the fronts stand proud of the carcass (ticks). */
  depthAdd: number;
  covered: Record<string, FrontKind>;
  stocks: StockUse[];
}

/**
 * The design's doors and drawers (src/families/fronts.ts) as parts, joints and hardware, cut from the carcass
 * stock (doors, drawer fronts, frames), the drawer-box ply, the back sheet (drawer bottoms) or glass to order.
 * Null when the design has no fronts, so a design without fronts builds exactly as before.
 */
export function frontsFor(design: Design, parts: readonly Part[], openings: readonly OpeningRow[], R: ResolvedStock, c: ConstructionProfile): FrontsResult | null {
  const specs = frontsOf(design as unknown as { fronts?: unknown; params?: Record<string, unknown> | null });
  if (!specs.length) return null;
  const out = buildFronts({ parts, openings, W: design.W, D: design.D, tf: R.thicknessTicks, backT: R.back.thicknessTicks, specs });
  const a = c.edgeAllowanceTicks, cutL = c.cutAllowanceTicks.l, cutW = c.cutAllowanceTicks.w;
  const arris = fmtNum(c.arrisMm);
  const drawerT = Math.round(FRONTS_PROFILE.drawerBox.thicknessMm * 10);
  const fits: SheetFit[] = [];
  const fp: Part[] = out.boards.map((q) => {
    const fit: FitCheck = q.stock === 'glass' ? { fits: true, rotated: false, note: 'Toughened glass, ordered to size.' }
      : q.stock === 'back' ? sheetFit(q.l + cutL, q.w + cutW, R.back.sheet, R.back.grainAxis, R.back.allowRotation, a)
        : q.stock === 'drawer' ? sheetFit(q.l + cutL, q.w + cutW, DRAWER_SHEET, 'length', true, a)
          : R.kind === 'sheet' ? sheetFit(q.l + cutL, q.w + cutW, R.sheet!, R.back.grainAxis, R.allowRotation, a) : boardFit(q.l + cutL, q.w + cutW, R);
    fits.push({ partId: q.id, fits: fit.fits, rotationUsed: fit.rotated, note: fit.note });
    const material = q.stock === 'glass' ? 'Toughened glass 4 mm' : q.stock === 'back' ? R.back.label : q.stock === 'drawer' ? FRONTS_PROFILE.drawerBox.material : R.label;
    const stockId = q.stock === 'glass' ? 'glass-4' : q.stock === 'back' ? `${R.id}:back` : q.stock === 'drawer' ? 'drawer-ply-12' : R.id;
    const face = q.stock === 'carcass' ? faceText(R, q.faceSide ?? 'out') : q.stock === 'back' ? R.back.faceText : q.stock === 'glass' ? 'Clear, polished edges' : 'Birch plywood 12 mm, B/BB; B face in';
    const edge = (q.stock === 'carcass' ? R.edgeText(arris) : q.stock === 'glass' ? 'Arrised and polished all round' : `Exposed ply edges, sanded arris ${arris} mm`) + (q.machining ? `; ${q.machining}` : '');
    const part: Part = {
      id: q.id, role: q.role, label: q.label, qty: 1, material, stockId, thicknessTicks: q.t,
      finished: { l: q.l, w: q.w, t: q.t }, cut: q.stock === 'glass' ? { l: q.l, w: q.w, t: q.t } : { l: q.l + cutL, w: q.w + cutW, t: q.t },
      grainAxis: 'l', grainWorld: q.grainWorld, face, edge,
      placement: { x: toMm(q.box.x), y: toMm(q.box.y), z: toMm(q.box.z), sx: toMm(q.box.sx), sy: toMm(q.box.sy), sz: toMm(q.box.sz) },
    };
    if (fit.glueUp) part.glueUp = fit.glueUp;
    return part;
  });
  for (const f of fits) {
    if (f.fits) continue;
    const pp = fp.find((q) => q.id === f.partId)!;
    out.errors.push({ field: 'fronts', message: `${pp.label}: ${f.note.charAt(0).toLowerCase()}${f.note.slice(1)}` });
  }
  const joints: Joint[] = out.joints.map((j) => ({ id: `${j.a}~${j.b}${j.kind === 'hinge' ? `#${j.face}` : ''}`, a: j.a, b: j.b, kind: j.kind, face: j.face, datumMm: toMm(j.datum), visible: VISIBLE_JOINTS.has(j.kind) }));
  const stocks: StockUse[] = [];
  if (out.boards.some((q) => q.stock === 'drawer')) stocks.push({ id: 'drawer-ply-12', version: '1.0', kind: 'sheet', material: 'Plywood', label: FRONTS_PROFILE.drawerBox.material, thicknessTicks: drawerT, sheet: DRAWER_SHEET, density: 680 });
  if (out.boards.some((q) => q.stock === 'glass')) stocks.push({ id: 'glass-4', version: '1.0', kind: 'order', material: 'Glass', label: 'Toughened glass 4 mm, to order', thicknessTicks: 40, density: 2500 });
  return { parts: fp, fits, joints, hardware: out.hardware, errors: out.errors, warnings: out.warnings, depthAdd: out.depthAdd, covered: out.covered, stocks };
}

const DIM_WORD = { W: 'width', H: 'height', D: 'depth' } as const;

/** The envelope fix for a size: "Set height to 2200 mm" (clamped, whole millimetres). */
export function clampFix(key: 'W' | 'H' | 'D', v: number, lim: { min: number; max: number }): IssueFix {
  const c = Number.isFinite(v) ? Math.min(lim.max, Math.max(lim.min, Math.round(v / 10) * 10)) : lim.max;
  return { label: `Set ${DIM_WORD[key]} to ${fmtMm(c)} mm`, patch: { [key]: c } };
}

/**
 * Can the boards be laid out at all? A design whose rules fail (a size out of range, a row under
 * the minimum height …) still generates every part, so the model, the parts list and the errors
 * show together; only sizes that are not positive finite numbers stop the layout (pilot-v1-r2 p11).
 */
export const layable = (d: Pick<Design, 'W' | 'H' | 'D'>): boolean => [d.W, d.H, d.D].every((v) => Number.isFinite(v) && v > 0);

/** The bundled thin stock offered when a thick board leaves no room ("Use thinner stock (birch plywood 18 mm)"). */
export const THIN_STOCK_ID = 'birch-ply-18';

/**
 * The fix for clear heights under the minimum: one row (tier, shelf) fewer when that leaves every
 * clear height at the minimum, else the thinner stock when that does, else none.
 * `clearWith(count, t)` gives the smallest clear height for a count and a board thickness (ticks).
 */
export function minHeightFix(o: { count: number; minCount: number; key: string; noun: string; t: number; thinT: number; min: number; currentStock: string; clearWith: (count: number, t: number) => number; patchFor?: (count: number) => Record<string, unknown> }): IssueFix | undefined {
  if (o.count - 1 >= o.minCount) {
    const g = o.clearWith(o.count - 1, o.t);
    if (g >= o.min) return { label: `Remove a ${o.noun} (${o.noun === 'shelf' ? 'openings' : o.noun === 'tier' ? 'tiers' : 'cells'} become ${fmtMm(Math.floor(g / 10) * 10)} mm)`, patch: o.patchFor ? o.patchFor(o.count - 1) : { [o.key]: o.count - 1 } };
  }
  if (o.currentStock !== THIN_STOCK_ID && o.thinT < o.t && o.clearWith(o.count, o.thinT) >= o.min) return { label: 'Use thinner stock (birch plywood 18 mm)', patch: { stockProfileId: THIN_STOCK_ID } };
  return undefined;
}

/** Range errors on W, H, D against a joint profile's envelope (ticks), each with a clamp fix. */
export function sizeErrors(design: Pick<Design, 'W' | 'H' | 'D'>, c: ConstructionProfile): Issue[] {
  const L = limitsOf(c);
  const out: Issue[] = [];
  for (const key of ['W', 'H', 'D'] as const) {
    const v = design[key];
    const lim = L[key];
    if (!Number.isFinite(v) || !Number.isInteger(v)) out.push({ field: key, message: `${lim.label} must be a number of millimetres.`, fix: clampFix(key, v, lim) });
    else if (v < lim.min || v > lim.max) out.push({ field: key, message: `${lim.label} must be between ${fmtMm(lim.min)} and ${fmtMm(lim.max)} mm for this style.`, fix: clampFix(key, v, lim) });
  }
  return out;
}

/** The stock a design names (bundled), with a passed profile of the same id winning. */
export function familyStock(design: Pick<Design, 'stockProfileId'>, stock: AnyStock | null | undefined, bundled: Readonly<Record<string, AnyStock>>, fallbackId: string): AnyStock {
  if (stock && stock.id === design.stockProfileId) return stock;
  return bundled[design.stockProfileId] ?? bundled[fallbackId]!;
}

/** A construction profile for this family: the passed one when it is the family's own, else the bundled one. */
export function familyJoints(familyId: string, passed: ConstructionProfile | undefined, own: ConstructionProfile): ConstructionProfile {
  return passed && (passed.familyId === familyId || passed.family === familyId) ? passed : own;
}

/** The case generator's output as family boards and joints (case joints carry mm datums). */
export function fromCase(out: { boards: readonly FamilyBoard[]; joints: readonly { a: string; b: string; kind: JointKind; face: string; datum: number }[] }): { boards: FamilyBoard[]; joints: FamilyJoint[] } {
  return {
    boards: out.boards.map((b) => ({ ...b, box: { ...b.box } })),
    joints: out.joints.map((j) => ({ ...j, datum: Math.round(j.datum * 10) })),
  };
}

/** Clear cell widths from divider centre-lines (ticks) inside a clear span x0…x1, dividers t thick. */
export function cellsFromCentres(x0: number, x1: number, centres: readonly number[], t: number): number[] {
  const h = Math.round(t / 2);
  const out: number[] = [];
  let at = x0;
  for (const c of centres) { out.push(c - h - at); at = c - h + t; }
  out.push(x1 - at);
  return out;
}
